/**
 * Сборка промптов — 01-SPEC.md §6 и docs/04-PROMPT-FIXTURES.md.
 *
 * Все функции чистые: ни БД, ни сети, ни файловой системы, ни `Date.now()`.
 * Промпты к image-моделям — всегда на английском (01-SPEC.md §7, «Локализация»).
 *
 * Правило сборки (`docs/04-PROMPT-FIXTURES.md` §1):
 * ```
 * normalize(part) = part.trim() без хвостовой точки
 * assemble(parts) = normalize(каждой непустой части).join(". ") + "."
 * ```
 */
import { z } from 'zod';
import { BookAttributesV1Schema, } from './attributes.js';
import { SPREAD_LAYOUTS, imageSpecFor, resolveLayout } from './layouts.js';
import { textBudgetFor } from './text-layout.js';
import { SCENE_REFERENCE_INSTRUCTION } from './scene-references.js';
/** Роль части промпта — для подсветки в журнале обращений (01-SPEC.md §5.11.1). */
export const PROMPT_PART_ROLES = [
    'style',
    'subject',
    'proportions',
    'revision',
    'layout',
    'suffix',
];
/** Суффиксы промптов (docs/04-PROMPT-FIXTURES.md §1). */
export const SCENE_PROMPT_SUFFIX_EN = 'same characters as reference';
export const LOCATION_PROMPT_SUFFIX_EN = 'establishing shot, wide view of the place, no people, no characters';
export const VIDEO_PROMPT_SUFFIX_EN = 'characters stay consistent, subtle motion';
export const COVER_PROMPT_SUFFIX_TEMPLATE_EN = 'full-bleed edge-to-edge illustration, no frame, no border, {page} {orientation} front book cover';
export const BACK_COVER_PROMPT_SUFFIX_TEMPLATE_EN = 'full-bleed edge-to-edge illustration, no frame, no border, no text, no title, no barcode, {page} {orientation} back book cover';
/** `normalize(part)` — обрезка пробелов и хвостовых точек. */
export function normalizePromptPart(part) {
    return part.trim().replace(/[.\s]+$/u, '');
}
/** `assemble(parts)` — соединение через «. » с точкой в конце. */
export function assemblePrompt(parts) {
    const normalized = parts.map(normalizePromptPart).filter((part) => part.length > 0);
    if (normalized.length === 0)
        return '';
    return `${normalized.join('. ')}.`;
}
function build(parts, negativePrompt) {
    const kept = parts
        .map((part) => ({ role: part.role, text: normalizePromptPart(part.text) }))
        .filter((part) => part.text.length > 0);
    return {
        prompt: assemblePrompt(kept.map((part) => part.text)),
        parts: kept,
        negativePrompt,
    };
}
// --- Входные данные -------------------------------------------------------
export const StyleForPromptSchema = z.object({
    promptPrefix: z.string().default(''),
    negativePromptImage: z.string().default(''),
    negativePromptVideo: z.string().default(''),
});
export const CharacterForPromptSchema = z.object({
    id: z.string().min(1),
    name: z.string().default(''),
    role: z.string().default(''),
    promptEn: z.string().default(''),
});
export const ThingForPromptSchema = z.object({
    id: z.string().min(1),
    name: z.string().default(''),
    descriptionRu: z.string().default(''),
    promptEn: z.string().default(''),
});
export const LocationForPromptSchema = z.object({
    id: z.string().min(1),
    name: z.string().default(''),
    descriptionRu: z.string().default(''),
    promptEn: z.string().default(''),
});
export const SceneForPromptSchema = z.object({
    index: z.number().int().min(0).default(0),
    layoutId: z.enum(['split', 'full-bleed', 'band']).nullable().default(null),
    bandImageRatio: z.number().gt(0).lt(1).nullable().default(null),
    textRu: z.string().default(''),
    promptEn: z.string().default(''),
    animationPromptEn: z.string().default(''),
    characterIds: z.array(z.string().min(1)).default([]),
    thingIds: z.array(z.string().min(1)).default([]),
    locationId: z.string().min(1).nullable().default(null),
});
export const BookForPromptSchema = z.object({
    title: z.string().default(''),
    subtitle: z.string().default(''),
    occasion: z.string().default(''),
    targetAgeYears: z.number().int().min(0).max(18).nullable().default(null),
    mustInclude: z.array(z.string()).default([]),
    mustAvoid: z.array(z.string()).default([]),
    sceneCount: z.number().int().min(1).default(8),
    coverPromptEn: z.string().default(''),
    backCoverPromptEn: z.string().default(''),
    attributes: BookAttributesV1Schema,
});
/**
 * Порядок референсов детерминированный и важен (01-SPEC.md §3.10.7):
 * главный герой → остальные герои по `order` → локация → вещи.
 */
export function sceneReferenceAssetIds(scene, refs) {
    const characters = (refs.characters ?? [])
        .filter((ref) => scene.characterIds.includes(ref.characterId))
        .slice()
        .sort((a, b) => {
        const main = Number(b.isMain ?? false) - Number(a.isMain ?? false);
        return main !== 0 ? main : a.order - b.order;
    });
    const location = scene.locationId
        ? (refs.locations ?? []).find((ref) => ref.locationId === scene.locationId)
        : undefined;
    const things = (refs.things ?? [])
        .filter((ref) => scene.thingIds.includes(ref.thingId))
        .slice()
        .sort((a, b) => a.order - b.order);
    const ordered = [
        ...characters.map((ref) => ref.assetId),
        ...(location ? [location.assetId] : []),
        ...things.map((ref) => ref.assetId),
    ];
    return ordered.filter((assetId, index) => ordered.indexOf(assetId) === index);
}
// --- Сборка ---------------------------------------------------------------
/** Лист персонажа: `style.promptPrefix` · `character.promptEn`. */
export function buildCharacterPrompt(character, style) {
    return build([
        { role: 'style', text: style.promptPrefix },
        { role: 'subject', text: character.promptEn },
    ], style.negativePromptImage);
}
/** Вещь: `style.promptPrefix` · `thing.promptEn` (fixtures §3.1). */
export function buildThingPrompt(thing, style) {
    return build([
        { role: 'style', text: style.promptPrefix },
        { role: 'subject', text: thing.promptEn },
    ], style.negativePromptImage);
}
/** Локация: префикс · `location.promptEn` · establishing shot. */
export function buildLocationPrompt(location, style) {
    return build([
        { role: 'style', text: style.promptPrefix },
        { role: 'subject', text: location.promptEn },
        { role: 'suffix', text: LOCATION_PROMPT_SUFFIX_EN },
    ], style.negativePromptImage);
}
/**
 * Разворот: префикс · `scene.promptEn` · подсказка раскладки · суффикс.
 *
 * Одна и та же сцена в раскладках `full-bleed` и `split` даёт разные промпты
 * и разные размеры; смена положения текста не меняет ни промпт, ни размер.
 */
export function buildScenePrompt(scene, book, style, refs = {}, revisionGuidance = '', characterDetails = []) {
    const layoutId = resolveLayout(scene, book.attributes);
    const layout = SPREAD_LAYOUTS[layoutId];
    const characterSummary = characterDetails
        .map((character) => {
        const details = [
            `type: ${character.kind}`,
            `gender: ${character.gender?.trim() || 'not specified'}`,
            ...(character.ageYears === null || character.ageYears === undefined
                ? []
                : [`age: ${character.ageYears} years`]),
            ...(character.role?.trim() ? [`family/story role: ${character.role.trim()}`] : []),
            ...(character.isLead ? ['story role: lead character'] : []),
        ];
        return `${character.name.trim()} [${details.join(', ')}]`;
    })
        .join('; ');
    const built = build([
        { role: 'style', text: style.promptPrefix },
        { role: 'subject', text: scene.promptEn },
        {
            role: 'proportions',
            text: characterSummary.length > 0
                ? `Character details: ${characterSummary}. Preserve each character's stated identity and family role. Keep height, body proportions, and relative scale appropriate for type and age`
                : '',
        },
        {
            role: 'revision',
            text: revisionGuidance
                ? `User-requested changes for this new variant: ${revisionGuidance}`
                : '',
        },
        { role: 'layout', text: layout.promptHintEn },
        { role: 'suffix', text: SCENE_PROMPT_SUFFIX_EN },
    ], style.negativePromptImage);
    return {
        ...built,
        layoutId,
        imageSpec: imageSpecFor(layoutId, book.attributes, scene),
        refAssetIds: sceneReferenceAssetIds(scene, refs),
    };
}
/**
 * Точечное редактирование готовой картинки: модель уже видит весь исходный кадр,
 * поэтому ей передаётся только новое пожелание, без старого сюжета, стиля
 * и референсов.
 */
export function buildSceneEditPrompt(scene, book, revisionGuidance, hasAdditionalReference = false) {
    const layoutId = resolveLayout(scene, book.attributes);
    const built = buildBookArtworkEditPrompt(revisionGuidance, hasAdditionalReference);
    return {
        ...built,
        layoutId,
        imageSpec: imageSpecFor(layoutId, book.attributes, scene),
        refAssetIds: [],
    };
}
/** Универсальная точечная правка уже готового изображения по текстовой инструкции. */
export function buildImageEditPrompt(revisionGuidance, hasAdditionalReference = false) {
    return build([
        {
            role: 'revision',
            text: hasAdditionalReference
                ? `The first provided image is the image to edit. Apply only this requested change: ${revisionGuidance}. Use the second provided image only as a visual reference for the requested details. Do not copy its composition or alter anything else`
                : `Apply only this requested change to the provided image: ${revisionGuidance}. Do not alter anything else`,
        },
    ], '');
}
/** Правка разворота или обложки меняет только то, что попросил пользователь. */
export function buildBookArtworkEditPrompt(revisionGuidance, hasAdditionalReference = false) {
    return buildImageEditPrompt(revisionGuidance, hasAdditionalReference);
}
/**
 * The page format as the image model reads it. ISO ids are words the model knows ("A4"); the
 * inch formats' ids are not, so they go in as their size.
 */
const PAGE_NAMES_EN = {
    LETTER: 'US Letter',
    PORTRAIT_8X10IN: '8x10 inch',
    SQUARE_8_5IN: '8.5x8.5 inch square',
};
function pageNameEn(attributes) {
    return PAGE_NAMES_EN[attributes.format.page] ?? attributes.format.page;
}
/** "portrait" or "landscape" — and nothing for a square page, which is neither. */
function orientationEn(attributes) {
    const { widthMm, heightMm } = attributes.format;
    return widthMm === heightMm ? '' : attributes.format.orientation;
}
function fillPage(template, attributes) {
    return template
        .replace('{page}', pageNameEn(attributes))
        .replace(' {orientation}', orientationEn(attributes) ? ` ${orientationEn(attributes)}` : '');
}
/** Суффикс обложки с подставленным форматом книги. */
export function coverPromptSuffix(attributes) {
    return fillPage(COVER_PROMPT_SUFFIX_TEMPLATE_EN, attributes);
}
/** Суффикс задней обложки с тем же физическим форматом страницы. */
export function backCoverPromptSuffix(attributes) {
    return fillPage(BACK_COVER_PROMPT_SUFFIX_TEMPLATE_EN, attributes);
}
/** Обложка: герой обязательно окружён любимыми вещами (§3.6, E7-T3). */
export function buildCoverPrompt(book, style, things = []) {
    const favouriteThings = [...things]
        .sort((left, right) => left.order - right.order)
        .map((thing) => normalizePromptPart(thing.promptEn))
        .filter(Boolean);
    return build([
        { role: 'style', text: style.promptPrefix },
        { role: 'subject', text: book.coverPromptEn },
        {
            role: 'subject',
            text: favouriteThings.length > 0
                ? `Clearly include the child's favourite things: ${favouriteThings.join(', ')}`
                : '',
        },
        {
            role: 'suffix',
            text: `Integrate the exact book title "${book.title}" into the artwork. Render this title once, clearly and legibly, preserving the exact spelling. Do not add any other text`,
        },
        { role: 'suffix', text: coverPromptSuffix(book.attributes) },
    ], negativePromptWithoutTypography(style.negativePromptImage));
}
/** Задняя обложка — отдельная полноразмерная иллюстрация без текста. */
export function buildBackCoverPrompt(book, style) {
    return build([
        { role: 'style', text: style.promptPrefix },
        { role: 'subject', text: book.backCoverPromptEn },
        { role: 'suffix', text: backCoverPromptSuffix(book.attributes) },
    ], style.negativePromptImage);
}
/** Общий негатив часто запрещает любой текст, что конфликтует с названием спереди. */
function negativePromptWithoutTypography(value) {
    return value
        .split(',')
        .map((part) => part.trim())
        .filter((part) => !/\b(?:text|lettering|letters|typography)\b/iu.test(part))
        .join(', ');
}
/**
 * Видео: `scene.animationPromptEn` · суффикс.
 * Префикс стиля в видео-промпт не входит (docs/04-PROMPT-FIXTURES.md §1).
 */
export function buildVideoPrompt(scene, style) {
    return build([
        { role: 'subject', text: scene.animationPromptEn },
        { role: 'suffix', text: VIDEO_PROMPT_SUFFIX_EN },
    ], style.negativePromptVideo);
}
/** Фиксированный объём AI-черновика, пока настройка числа разворотов скрыта. */
export const DEFAULT_STORY_SCENE_COUNT = 8;
const STORY_LANGUAGE_NAMES = {
    ru: 'Russian',
    en: 'English',
    uz: 'Uzbek',
    ko: 'Korean',
    ka: 'Georgian',
    tr: 'Turkish',
    tt: 'Tatar',
    fr: 'French',
    es: 'Spanish',
};
/** Бриф генератору сюжета (01-SPEC.md §3.9). */
export function buildStoryBrief(prompt, book, characters, things = [], locations = [], options = {}) {
    const layoutId = book.attributes.layout.default;
    const budget = textBudgetFor(layoutId, book.attributes);
    const layout = SPREAD_LAYOUTS[layoutId];
    const brief = {
        language: book.language,
        mainIdea: options.mainIdea?.trim() ?? '',
        occasion: book.occasion,
        targetAgeYears: book.targetAgeYears,
        sceneCount: book.sceneCount,
        mustInclude: [...book.mustInclude],
        mustAvoid: [...book.mustAvoid],
        characters: characters.map((character) => ({ ...character })),
        things: things.map((thing) => ({ ...thing })),
        locations: locations.map((location) => ({ ...location })),
        layout: {
            id: layoutId,
            textSlots: layout.textSlots,
            maxCharsPerLine: budget.capacity.maxCharsPerLine,
            maxLines: budget.capacity.maxLines,
            charsPerSpread: budget.perSpread,
            charsPerBlock: budget.perBlock,
        },
    };
    return {
        brief,
        systemPrompt: `${prompt.system}\n\n${SCENE_REFERENCE_INSTRUCTION}`,
        userPrompt: [
            `LANGUAGE REQUIREMENT: Write the book title and every textRu value only in ${STORY_LANGUAGE_NAMES[brief.language]} (${brief.language}).`,
            prompt.user(brief),
        ].join('\n'),
        formatInstruction: prompt.format(brief),
        retryInstruction: prompt.retry,
    };
}
//# sourceMappingURL=prompts.js.map